import { useMemo } from 'react';

import { M_REGULAR_COLLECTION_URLS } from 'constants/apiConstants';
import { STEP_CATALOGUE_GC_PAGE_SIZE } from 'constants/appConstants';
import {
  ADJUVANT_COLLECTION,
  CHEMICAL_COLLECTION,
  REGULAR_COLLECTIONS,
  SEED_TREATMENT_COLLECTION,
  SEED_TREATMENT_PRODUCT_GROUP_NAME,
} from 'constants/gcConstants';
import {
  G_ADJUVANT_QUERY_KEY,
  G_CHEMICAL_QUERY_KEY,
  G_SEED_TREATMENT_QUERY_KEY,
  M_ADJUVANT_QUERY_KEY,
  M_CHEMICAL_QUERY_KEY,
  M_SEARCH_SEED_TREATMENT_QUERY_KEY,
  M_SEARCH_ADJUVANT_QUERY_KEY,
  M_SEARCH_CHEMICAL_QUERY_KEY,
  M_SEED_TREATMENT_QUERY_KEY,
} from 'constants/queryKeys';
import { useCatalogueDropdown } from 'hooks/useCatalogueDropdown';
import { useMOrgId } from 'hooks/useMOrgId';
import { GPlantProtection } from 'interfaces/IGlobalCatalogue';
import { PoisonSchedule } from 'interfaces/IInputStep';
import { MPlantProtection, MSearchFilter } from 'interfaces/IMasterDb';
import { composeList } from 'utils/catalogueMergeUtils';

import { StepSelectorOption } from './stepConfig';

const toStepSelectorOption = (item: GPlantProtection): StepSelectorOption => ({
  label: item.name,
  item: item.name,
  globalId: item.globalId,
  rate: item.rate ?? null,
  cost: item.price ?? null,
  unit: item.rateUnit ?? null,
  useWater: item.useWater ?? null,
  productGroups: item.productGroups,
  activeConstituents: item.activeConstituents,
  formulationCode: item.formulationCode ?? null,
  formulation: item.formulation ?? null,
  apvmaNumber: item.apvmaNumber ?? null,
  poisonSchedule: item.poisonSchedule as PoisonSchedule,
  withholdPeriodGrazing: item.withholdPeriodGrazing ?? null,
  withholdPeriodCutting: item.withholdPeriodCutting ?? null,
  withholdPeriodHarvest: item.withholdPeriodHarvest ?? null,
  withholdPeriodPlantback: item.withholdPeriodPlantback ?? null,
  specWithholdPeriods: item.specWithholdPeriods,
  reentry: item.reentry ?? null,
  reentryUnit: item.reentryUnit ?? null,
  reentryNotes: item.reentryNotes ?? null,
  manufacturerId: item.manufacturerId ?? null,
  snapManufacturer: item.snapManufacturer ?? null,
  supplierId: item.supplierId ?? null,
  snapSupplier: item.snapSupplier ?? null,
});

// MSearch scope per tab. Search stays on the physical GPlantProtection
// collection (MSearch resolves the collection name to a real Mongo collection,
// so the GChemical/GAdjuvant route names can't be searched) and is narrowed
// with a useWater condition. Chemical uses "!= true" rather than "= false" so
// products with no useWater set stay on the Plant Protection tab, matching
// the GChemical route's $ne filter.
const CHEMICAL_SEARCH_FILTERS: MSearchFilter[][] = [
  [{ field: 'useWater', operator: '!=', value: 'true' }],
];
const ADJUVANT_SEARCH_FILTERS: MSearchFilter[][] = [
  [{ field: 'useWater', operator: '=', value: 'true' }],
];
// Seed treatments are scoped by product group, not useWater (OP-2401).
const SEED_TREATMENT_SEARCH_FILTERS: MSearchFilter[][] = [
  [
    {
      field: 'productGroups.snapProductGroup',
      operator: '=',
      value: SEED_TREATMENT_PRODUCT_GROUP_NAME,
    },
  ],
];

interface PlantProtectionVariant {
  gCollectionKey: string;
  mCollectionKey: keyof typeof M_REGULAR_COLLECTION_URLS;
  gQueryKey: string;
  mQueryKey: string;
  searchQueryKey: string;
  searchFilters: MSearchFilter[][];
}

const CHEMICAL_VARIANT: PlantProtectionVariant = {
  gCollectionKey: CHEMICAL_COLLECTION,
  mCollectionKey: CHEMICAL_COLLECTION,
  gQueryKey: G_CHEMICAL_QUERY_KEY,
  mQueryKey: M_CHEMICAL_QUERY_KEY,
  searchQueryKey: M_SEARCH_CHEMICAL_QUERY_KEY,
  searchFilters: CHEMICAL_SEARCH_FILTERS,
};

const ADJUVANT_VARIANT: PlantProtectionVariant = {
  gCollectionKey: ADJUVANT_COLLECTION,
  mCollectionKey: ADJUVANT_COLLECTION,
  gQueryKey: G_ADJUVANT_QUERY_KEY,
  mQueryKey: M_ADJUVANT_QUERY_KEY,
  searchQueryKey: M_SEARCH_ADJUVANT_QUERY_KEY,
  searchFilters: ADJUVANT_SEARCH_FILTERS,
};

const SEED_TREATMENT_VARIANT: PlantProtectionVariant = {
  gCollectionKey: SEED_TREATMENT_COLLECTION,
  mCollectionKey: SEED_TREATMENT_COLLECTION,
  gQueryKey: G_SEED_TREATMENT_QUERY_KEY,
  mQueryKey: M_SEED_TREATMENT_QUERY_KEY,
  searchQueryKey: M_SEARCH_SEED_TREATMENT_QUERY_KEY,
  searchFilters: SEED_TREATMENT_SEARCH_FILTERS,
};

// Each tab gets its own instance — its own scoped G/M browse routes
// (GChemical/MChemical, GAdjuvant/MAdjuvant, GSeedTreatment/MSeedTreatment), search term and MSearch cache —
// so switching tabs never re-triggers the other tab's queries (OP-2420). Every
// source is scoped server-side, so no client-side useWater split is applied:
// M documents only carry useWater when it was explicitly overridden, so a
// client check could wrongly drop server-scoped M-only items.
const usePlantProtectionCatalogue = (
  gOrgId: string | null,
  {
    gCollectionKey,
    mCollectionKey,
    gQueryKey,
    mQueryKey,
    searchQueryKey,
    searchFilters,
  }: PlantProtectionVariant
) => {
  const mOrgId = useMOrgId();

  const {
    items,
    isLoading,
    isSearchFetching,
    isPaginating: isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    hasFetchNextPageError,
    searchTerm,
    onSearch,
    clearSearch,
  } = useCatalogueDropdown<
    GPlantProtection,
    MPlantProtection,
    GPlantProtection
  >({
    gOrgId,
    mOrgId,
    gCollectionKey,
    mCollectionKey,
    gSearchCollectionName: REGULAR_COLLECTIONS.plantProtection,
    gQueryKey: [gQueryKey],
    mQueryKey: [mQueryKey],
    searchQueryKey: [searchQueryKey],
    searchFilters,
    mergeItems: composeList<GPlantProtection, MPlantProtection>,
    gPageSize: STEP_CATALOGUE_GC_PAGE_SIZE,
  });

  const options = useMemo(() => items.map(toStepSelectorOption), [items]);

  return {
    options,
    isLoading,
    isSearchFetching,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    hasFetchNextPageError,
    searchTerm,
    onSearch,
    clearSearch,
  };
};

// Plant Protection tab — chemical products (useWater not true).
export const useChemicalCatalogue = (gOrgId: string | null) =>
  usePlantProtectionCatalogue(gOrgId, CHEMICAL_VARIANT);

// Adjuvant tab — products mixed into the water carrier (useWater = true).
export const useAdjuvantCatalogue = (gOrgId: string | null) =>
  usePlantProtectionCatalogue(gOrgId, ADJUVANT_VARIANT);

// Seed Treatment tab — products in the seed treatment product group.
export const useSeedTreatmentCatalogue = (gOrgId: string | null) =>
  usePlantProtectionCatalogue(gOrgId, SEED_TREATMENT_VARIANT);
